import { useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { logger } from '@/utils/logger';
import { useUser } from '@/constants/UserContext';

/**
 * The organization to switch an admin into so they can act on a linked event, or null to
 * leave things as they are.
 *
 * Only when the event belongs to an organization they administer and they are not already
 * acting as it. Organization accounts never switch: they are always their own org.
 */
export function orgToActAs(input: {
  eventOrgId: string | null | undefined;
  adminOrgIds: string[];
  activeAdminOrgId: string | null;
  isOrganization: boolean;
}): string | null {
  const { eventOrgId, adminOrgIds, activeAdminOrgId, isOrganization } = input;
  if (isOrganization || !eventOrgId) return null;
  if (eventOrgId === activeAdminOrgId) return null;
  return adminOrgIds.includes(eventOrgId) ? eventOrgId : null;
}

/**
 * Follow a link to an event on the Volunteers page as that event's organization.
 *
 * Admins of an organization receive its shift notifications ("Shift dropped", "Shift
 * changed", "Shift picked up"), and those open /volunteers?eventId=…&tab=shifts. But that
 * page only loads events while acting as an org, so an admin in their normal volunteer view
 * landed on an empty page. The link names the event, not the mode -- so look up whose event
 * it is and switch into that org if they administer it.
 */
export function useActAsEventOrg(eventId: string | null) {
  const { user, adminOrgs, activeAdminOrgId, setActiveAdminOrgId } = useUser();
  const isOrganization = user?.role === 'organization';
  // A string, so a reload of the same admin orgs (a new array) does not count as a change.
  const adminOrgIdsKey = adminOrgs.map((o) => o.id).join(',');

  // Act once per link. Re-deciding whenever the org changed would pull an admin who switched
  // back to their personal view straight into the org again while the link is still in the URL.
  const handledEventIdRef = useRef<string | null>(null);
  // Read at decision time, deliberately not a dependency (see above).
  const activeAdminOrgIdRef = useRef(activeAdminOrgId);
  activeAdminOrgIdRef.current = activeAdminOrgId;

  useEffect(() => {
    if (!eventId || isOrganization || !adminOrgIdsKey) return;
    if (handledEventIdRef.current === eventId) return;
    handledEventIdRef.current = eventId;

    void (async () => {
      const { data, error } = await supabase.from('events').select('created_by').eq('id', eventId).maybeSingle();
      // A newer link took over while this one was loading.
      if (handledEventIdRef.current !== eventId) return;
      if (error) {
        logger.warn('useActAsEventOrg: could not read the linked event', { eventId, error });
        return;
      }
      const next = orgToActAs({
        eventOrgId: (data as { created_by?: string } | null)?.created_by,
        adminOrgIds: adminOrgIdsKey.split(','),
        activeAdminOrgId: activeAdminOrgIdRef.current,
        isOrganization,
      });
      if (next) setActiveAdminOrgId(next);
    })();
  }, [eventId, isOrganization, adminOrgIdsKey, setActiveAdminOrgId]);
}
